"use client";

import { Badge } from "@/components/ui/badge";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { PageHeader } from "@/components/page-header";
import { supabase } from "@/lib/supabase";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

// ─── Types ─────────────────────────────────────────────────────────────────────

type SyncRun = {
  id: string;
  ran_at: string;
  source: "manual" | "cron";
  status: "success" | "aborted" | "error";
  fetched_count: number | null;
  created_count: number | null;
  updated_count: number | null;
  flagged_inactive_count: number | null;
  ignored_count: number | null;
  message: string | null;
  triggered_by: string | null;
};

type SyncEventChanges = Record<string, { from: unknown; to: unknown }> | null;

type SyncEvent = {
  id: string;
  user_id: string | null;
  email: string;
  action: "created" | "updated" | "flagged_inactive" | "ignored";
  changes: SyncEventChanges;
  created_at: string;
};

type TriggeredByProfile = { user_id: string; display_name: string | null; username: string | null };

// ─── Constants & helpers ───────────────────────────────────────────────────────

const PAGE_SIZE = 25;

const FIELD_LABELS: Record<string, string> = {
  display_name: "Name",
  job_role: "Position",
  team: "Department",
  employment_status: "Status",
  reason: "Reason",
};

const formatDateTime = (iso: string | null) => {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
};

const sourceLabel = (source: SyncRun["source"]) => (source === "cron" ? "Cron" : "Manual");

const statusLabel = (status: SyncRun["status"]) => {
  if (status === "success") return "Success";
  if (status === "aborted") return "Aborted";
  return "Error";
};

const statusBadgeVariant = (status: SyncRun["status"]): "default" | "secondary" | "destructive" => {
  if (status === "success") return "default";
  if (status === "aborted") return "secondary";
  return "destructive";
};

const actionLabel = (action: SyncEvent["action"]) => {
  if (action === "created") return "Created";
  if (action === "updated") return "Updated";
  if (action === "flagged_inactive") return "Flagged inactive";
  return "Ignored";
};

const actionBadgeVariant = (action: SyncEvent["action"]): "default" | "secondary" | "outline" => {
  if (action === "created") return "default";
  if (action === "updated") return "secondary";
  return "outline";
};

const formatChangeValue = (value: unknown): string => (value === null || value === undefined ? "—" : String(value));

function ChangesSummary({ changes }: { changes: SyncEventChanges }) {
  const entries = changes ? Object.entries(changes) : [];
  if (entries.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="space-y-0.5">
      {entries.map(([field, { from, to }]) => (
        <div key={field} className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{FIELD_LABELS[field] ?? field}: </span>
          {formatChangeValue(from)} → {formatChangeValue(to)}
        </div>
      ))}
    </div>
  );
}

// ─── Run detail (per-person events) ────────────────────────────────────────────

function SyncRunDetail({ run, onBack }: { run: SyncRun; onBack: () => void }) {
  const [events, setEvents] = useState<SyncEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void supabase
      .from("project_central_sync_events")
      .select("id, user_id, email, action, changes, created_at")
      .eq("sync_log_id", run.id)
      .order("email")
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error("[SyncRunDetail] load events error:", error);
          toast.error("Failed to load sync events.");
        } else {
          setEvents((data ?? []) as SyncEvent[]);
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [run.id]);

  const columns: DataTableColumn<SyncEvent>[] = [
    { header: "Email", width: "w-[260px]", render: (e) => <span className="truncate">{e.email}</span> },
    {
      header: "Action",
      width: "w-[140px]",
      render: (e) => (
        <Badge variant={actionBadgeVariant(e.action)} className="text-xs">
          {actionLabel(e.action)}
        </Badge>
      ),
    },
    { header: "Changes", render: (e) => <ChangesSummary changes={e.changes} /> },
  ];

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        title={`Sync run — ${formatDateTime(run.ran_at)}`}
        description={`${sourceLabel(run.source)} · ${statusLabel(run.status)}${run.message ? ` · ${run.message}` : ""}`}
        onBack={onBack}
        showBack
      />
      <div className="flex-1 overflow-auto p-6">
        <div className="mx-auto w-full max-w-4xl">
          <DataTable
            columns={columns}
            data={events}
            getRowKey={(e) => e.id}
            loading={loading}
            searchFilter={(e, q) => e.email.toLowerCase().includes(q)}
            searchPlaceholder="Search by email…"
            emptyMessage="No changes recorded for this run."
            itemLabel="change"
            pageSize={PAGE_SIZE}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Run list ──────────────────────────────────────────────────────────────────

export type SyncHistoryPanelProps = {
  onBack?: () => void;
};

export function SyncHistoryPanel({ onBack }: SyncHistoryPanelProps) {
  const [runs, setRuns] = useState<SyncRun[]>([]);
  const [triggeredByProfiles, setTriggeredByProfiles] = useState<Map<string, TriggeredByProfile>>(new Map());
  const [loading, setLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState<SyncRun | null>(null);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("project_central_sync_log")
        .select(
          "id, ran_at, source, status, fetched_count, created_count, updated_count, flagged_inactive_count, ignored_count, message, triggered_by"
        )
        .order("ran_at", { ascending: false });
      if (error) throw error;

      const runRows = (data ?? []) as SyncRun[];
      setRuns(runRows);

      const triggeredByIds = Array.from(
        new Set(runRows.map((r) => r.triggered_by).filter((id): id is string => id !== null))
      );
      if (triggeredByIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select("user_id, display_name, username")
          .in("user_id", triggeredByIds);
        if (profilesError) throw profilesError;
        setTriggeredByProfiles(new Map((profiles ?? []).map((p) => [p.user_id as string, p as TriggeredByProfile])));
      } else {
        setTriggeredByProfiles(new Map());
      }
    } catch (err) {
      console.error("[SyncHistoryPanel] load runs error:", err);
      toast.error("Failed to load sync history.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  if (selectedRun) {
    return <SyncRunDetail run={selectedRun} onBack={() => setSelectedRun(null)} />;
  }

  const columns: DataTableColumn<SyncRun>[] = [
    {
      header: "Ran at",
      width: "w-[170px]",
      sortKey: "ran_at",
      sortValue: (r) => new Date(r.ran_at).getTime(),
      render: (r) => <span className="text-muted-foreground">{formatDateTime(r.ran_at)}</span>,
    },
    {
      header: "Source",
      width: "w-[100px]",
      render: (r) => (
        <Badge variant={r.source === "cron" ? "outline" : "secondary"} className="text-xs">
          {sourceLabel(r.source)}
        </Badge>
      ),
    },
    {
      header: "Status",
      width: "w-[110px]",
      render: (r) => (
        <Badge variant={statusBadgeVariant(r.status)} className="text-xs">
          {statusLabel(r.status)}
        </Badge>
      ),
    },
    {
      header: "Triggered by",
      width: "w-[180px]",
      render: (r) => {
        if (!r.triggered_by) return <span className="text-muted-foreground">System (cron)</span>;
        const profile = triggeredByProfiles.get(r.triggered_by);
        const name = profile?.display_name?.trim() || profile?.username?.trim() || "Unknown";
        return <span className="truncate">{name}</span>;
      },
    },
    {
      header: "Fetched",
      width: "w-[80px]",
      align: "right",
      render: (r) => r.fetched_count ?? "—",
    },
    {
      header: "Created",
      width: "w-[80px]",
      align: "right",
      render: (r) => r.created_count ?? "—",
    },
    {
      header: "Updated",
      width: "w-[80px]",
      align: "right",
      render: (r) => r.updated_count ?? "—",
    },
    {
      header: "Flagged",
      width: "w-[80px]",
      align: "right",
      render: (r) => r.flagged_inactive_count ?? "—",
    },
    {
      header: "Ignored",
      width: "w-[80px]",
      align: "right",
      render: (r) => r.ignored_count ?? "—",
    },
    {
      header: "Message",
      render: (r) => <span className="truncate text-xs text-muted-foreground">{r.message ?? "—"}</span>,
    },
  ];

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        title="Sync history"
        description="Every Project Central sync run — cron and manual — and exactly who and what changed."
        onBack={onBack}
      />
      <div className="flex-1 overflow-auto p-6">
        <div className="mx-auto w-full max-w-6xl">
          <DataTable
            columns={columns}
            data={runs}
            getRowKey={(r) => r.id}
            loading={loading}
            onRefresh={() => void loadRuns()}
            onRowClick={setSelectedRun}
            emptyMessage="No sync runs yet."
            itemLabel="run"
            pageSize={PAGE_SIZE}
          />
        </div>
      </div>
    </div>
  );
}
